import type {
  AlertState,
  DeviceStatus,
  OSFamily,
  PatchStatus,
  Severity,
} from "../../api/types";
import { Badge, type BadgeTone } from "../ui/Badge";
import { OsLogo } from "./OsLogo";

const deviceStatusLabels: Record<DeviceStatus, string> = {
  online: "Çevrimiçi",
  offline: "Çevrimdışı",
  degraded: "Bozuk",
  unknown: "Bilinmiyor",
};

const deviceStatusTones: Record<DeviceStatus, BadgeTone> = {
  online: "success",
  offline: "danger",
  degraded: "warning",
  unknown: "muted",
};

export function DeviceStatusBadge({ status }: { status: DeviceStatus }) {
  return (
    <Badge tone={deviceStatusTones[status]}>
      <span
        aria-hidden="true"
        className={
          status === "online"
            ? "bg-success h-1.5 w-1.5 rounded-full"
            : status === "offline"
              ? "bg-danger h-1.5 w-1.5 rounded-full"
              : status === "degraded"
                ? "bg-warning h-1.5 w-1.5 rounded-full"
                : "bg-muted-foreground h-1.5 w-1.5 rounded-full"
        }
      />
      {deviceStatusLabels[status]}
    </Badge>
  );
}

const severityLabels: Record<Severity, string> = {
  critical: "Kritik",
  high: "Yüksek",
  medium: "Orta",
  low: "Düşük",
  unknown: "Bilinmiyor",
};

const severityTones: Record<Severity, BadgeTone> = {
  critical: "danger",
  high: "warning",
  medium: "info",
  low: "muted",
  unknown: "muted",
};

export function SeverityBadge({ severity }: { severity: Severity }) {
  return <Badge tone={severityTones[severity]}>{severityLabels[severity]}</Badge>;
}

const patchStatusLabels: Record<PatchStatus, string> = {
  installed: "Yüklü",
  pending: "Bekliyor",
  hidden: "Gizli",
  failed: "Başarısız",
};

const patchStatusTones: Record<PatchStatus, BadgeTone> = {
  installed: "success",
  pending: "warning",
  hidden: "muted",
  failed: "danger",
};

export function PatchStatusBadge({ status }: { status: PatchStatus }) {
  return <Badge tone={patchStatusTones[status]}>{patchStatusLabels[status]}</Badge>;
}

const alertStateLabels: Record<AlertState, string> = {
  firing: "Tetiklendi",
  resolved: "Çözüldü",
  acknowledged: "Onaylandı",
  suppressed: "Sessize alındı",
};

const alertStateTones: Record<AlertState, BadgeTone> = {
  firing: "danger",
  resolved: "success",
  acknowledged: "info",
  suppressed: "muted",
};

export function AlertStateBadge({ state }: { state: AlertState }) {
  return <Badge tone={alertStateTones[state]}>{alertStateLabels[state]}</Badge>;
}

const osLabels: Record<OSFamily, string> = {
  windows: "Windows",
  linux: "Linux",
  snmp: "SNMP",
};

export function OsBadge({ osFamily }: { osFamily: OSFamily }) {
  return (
    <Badge tone={osFamily === "windows" ? "info" : osFamily === "linux" ? "default" : "warning"}>
      <OsLogo osFamily={osFamily} className="h-3 w-3" />
      {osLabels[osFamily]}
    </Badge>
  );
}